import { useMutation } from '@tanstack/react-query';
import { useCookies } from 'react-cookie';
import packagejson from '../../../../package.json';

interface VersionNotificationMutationOptions {
  onNotify: () => void;
}

/**
 * mutation handles version mismatch detection and cookie management
 * when a version mismatch is detected and the mutation is triggered,
 * updating the version cookie and callingthe onNotify callback.
 */
export const useVersionNotificationMutation = ({
  onNotify,
}: VersionNotificationMutationOptions) => {
  const [major, minor] = packagejson.version.split('.');
  const majorMinor = `${major}.${minor}`;
  const [cookies, setCookies, removeCookies] = useCookies(['version']);

  const isVersionMismatch = !cookies.version || cookies.version !== majorMinor;

  const mutation = useMutation({
    mutationKey: ['version-notification'],
    mutationFn: async () => {
      // remove stale cookie
      removeCookies('version');

      // set new cookie with 3-month expiry
      const date = new Date();
      setCookies('version', majorMinor, {
        path: '/',
        expires: new Date(date.setMonth(date.getMonth() + 3)),
      });
    },
    onSuccess: () => {
      onNotify();
    },
  });

  // trigger mutation if version mismatch and not already handled
  if (isVersionMismatch && mutation.isIdle) {
    mutation.mutate();
  }

  return {
    isVersionMismatch,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
  };
};

export default useVersionNotificationMutation;
